import { QuestionItem, ValidationFormat, ValidationRules } from "../types/form";
import {
  hasDateBounds,
  resolveDateBound,
  resolveMinDateFromAnswers,
} from "./dateRangeValidation";
import { hasCompositeValidationConstraints } from "./compositeValidation";

/** Read validationRules from a question (including nested originalQuestion). */
export function getValidationRules(
  question: QuestionItem,
): ValidationRules | null {
  const rules =
    question.validationRules ??
    question.originalQuestion?.validationRules ??
    null;
  if (!rules || typeof rules !== "object" || Array.isArray(rules)) {
    return null;
  }
  return rules as ValidationRules;
}

export function getValidationFormat(
  question: QuestionItem,
): ValidationFormat | undefined {
  return getValidationRules(question)?.format;
}

export function hasRequiredValue(rules: ValidationRules | null): boolean {
  return (
    rules?.requiredValue != null && String(rules.requiredValue).trim() !== ""
  );
}

export function hasValidationConstraints(question: QuestionItem): boolean {
  const rules = getValidationRules(question);
  if (!rules) return false;
  if (rules.format) return true;
  if (hasRequiredValue(rules)) return true;
  if (hasDateBounds(rules)) return true;
  return hasCompositeValidationConstraints(question);
}

/** Resolved min/max for HTML date inputs (YYYY-MM-DD). */
export function getDateBoundsForInput(
  question: QuestionItem,
  formData?: Record<string, unknown>,
): { min?: string; max?: string } {
  const rules = getValidationRules(question);
  if (!rules) return {};

  const staticMin = resolveDateBound(
    typeof rules.minDate === "string" ? rules.minDate : undefined,
  );
  const minFrom = resolveMinDateFromAnswers(rules, formData);
  const min =
    staticMin && minFrom
      ? staticMin > minFrom
        ? staticMin
        : minFrom
      : staticMin || minFrom;
  const max = resolveDateBound(
    typeof rules.maxDate === "string" ? rules.maxDate : undefined,
  );

  return {
    ...(min ? { min } : {}),
    ...(max ? { max } : {}),
  };
}

export function getNumberInputConstraints(
  question: QuestionItem,
): { min?: number; max?: number; step?: number } {
  const rules = getValidationRules(question);
  if (!rules) return {};

  return {
    ...(typeof rules.min === "number" ? { min: rules.min } : {}),
    ...(typeof rules.max === "number" ? { max: rules.max } : {}),
    ...(typeof rules.step === "number"
      ? { step: rules.step }
      : rules.format === "non_negative_integer"
        ? { step: 1 }
        : {}),
  };
}
